import { ChevronDownIcon } from "@radix-ui/react-icons";
import type { Editor, JSONContent } from "@tiptap/core";
import { CircleAlert, Pencil, RefreshCw, X } from "lucide-react";
import type { Root as MdastRoot } from "mdast";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { Language, ReportSection, ReportSectionMeta } from "~/api/model";
import { ContentCopyButton } from "~/components/ContentCopyButton";
import { LoadingSpinner } from "~/components/Icons";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { NoteEditor } from "~/features/notes/components/NoteEditor";
import { useChatDocumentParams } from "~/helpers";
import { toast } from "~/hooks/use-toast";
import { useLazyRender } from "~/hooks/useLazyRender";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/analytics";
import { htmlToMarkdown } from "~/utils/htmlToMarkdown";
import logger from "~/utils/logger";
import {
  BatesLink,
  SectionLink,
  type SectionClickHandler,
} from "~/utils/markdownTerms";
import type { TermMatcher } from "~/utils/termMatcher";
import { Hint } from "../../types";
import { FormatSectionInput } from "./FormatSectionInput";
import { localizeReportSectionTitle } from "./localizeReportSectionTitle";
import { reportMdastToTiptap, tiptapToReportMdast } from "./mdast/tiptapMdast";
import {
  ReleaseModeReportContent,
  StableReportContent,
} from "./ReleaseModeReportContent";
import { reportMdastEditorExtensions } from "./reportMdastEditorExtensions";
import type { ReportReferenceDocumentOption } from "./reportReferenceSlashCommand";
import { ReportReferenceSlashCommand } from "./reportReferenceSlashCommand";
import { ReportReferenceSlashMenu } from "./ReportReferenceSlashMenu";
import { ReportSectionSources, SourceReference } from "./ReportSectionSources";
import { SourceMapLink } from "./sourceMapLinkExtension";
import { useFormatSection } from "./useFormatSection";
import { useSectionData } from "./useSectionData";

export type ReportSectionViewProps = {
  reportSection: ReportSectionMeta;
  accordionValue: string;
  terms: Hint[];
  medicationMatcher?: TermMatcher | null;
  sectionLinks?: SectionLink[];
  reportId: string;
  isExpanded: boolean;
  templateName?: string | null;
  outputLanguage?: Language | null;
  isActionAvailable?: boolean;
  /**
   * True when the parent report is outdated because the DocList or template
   * has changed since the report was generated. In that state, regenerating
   * a single section in isolation would mix new template/docs with stale
   * neighbouring sections, so the section-level Rewrite action is disabled
   * and the user is steered to the top-level "Rewrite" banner instead.
   */
  isReportOutdated?: boolean;
  /**
   * When true the section content is treated as already visible on mount,
   * bypassing the IntersectionObserver. Pass this for the first few
   * sections that are guaranteed to be in the viewport so they start
   * fetching data immediately instead of waiting for a scroll event.
   */
  initiallyVisible?: boolean;
  onSourceClick?: (source: SourceReference) => void;
  onSectionClick?: SectionClickHandler;
  batesLinks?: BatesLink[];
  onBatesClick?: (pageId: string) => void;
  referenceDocuments?: ReportReferenceDocumentOption[];
  /**
   * Overrides how the non-editing section body is rendered (§5). Defaults to the
   * v1 markdown renderer ({@link ReportSectionMarkdownV1}); the v2 renderer
   * ({@link ReportSectionMdastView}) injects an MDAST-based renderer with a
   * per-section v1 fallback. Everything else (accordion, edit, actions) is
   * shared unchanged.
   */
  renderDisplayContent?: (sectionData: ReportSection | undefined) => ReactNode;
  /**
   * Derives the MDAST tree persisted after the formatting action. The MDAST
   * renderer supplies this so formatted output is saved only as `contentMdast`;
   * v1 omits it and continues saving Markdown `content`.
   */
  deriveContentMdast?: (markdown: string) => MdastRoot;
  getEditMdast?: (sectionData: ReportSection | undefined) => MdastRoot | null;
};

/**
 * v1 report body renderer: markdown string → `HighlightedMarkdown` with the
 * report citation grouping plugin and citation components. Shared as the
 * default display renderer and the v2 per-section fallback so both paths render
 * identically when no valid MDAST is available.
 */
function hasTiptapContent(node: JSONContent | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return Boolean(node.text?.trim());
  if (node.type === "image" || node.type === "sourceMapLink") return true;
  return node.content?.some(hasTiptapContent) ?? false;
}

export const ReportSectionMarkdownV1 = ({
  content,
  terms,
  medicationMatcher,
}: {
  content: string;
  terms: Hint[];
  medicationMatcher?: TermMatcher | null;
}): ReactNode => (
  <StableReportContent
    content={content}
    terms={terms}
    medicationMatcher={medicationMatcher}
  />
);

export const ReportSectionView = memo(
  ({
    reportSection,
    accordionValue,
    terms,
    medicationMatcher,
    reportId,
    isExpanded,
    templateName,
    outputLanguage,
    isActionAvailable = true,
    isReportOutdated = false,
    initiallyVisible = false,
    onSourceClick,
    referenceDocuments = [],
    renderDisplayContent,
    deriveContentMdast,
    getEditMdast,
  }: ReportSectionViewProps) => {
    const { formatMessage } = useIntl();
    const { organizationId, caseId } = useChatDocumentParams();
    const imageInputRef = useRef<HTMLInputElement>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState("");
    const [editJsonContent, setEditJsonContent] = useState<JSONContent>();
    const [isSaving, setIsSaving] = useState(false);
    const [isImageUploadPending, setIsImageUploadPending] = useState(false);
    const [referenceMenuEditor, setReferenceMenuEditor] =
      useState<Editor | null>(null);
    const { ref: lazyRef, hasBeenVisible } = useLazyRender(
      "400px",
      initiallyVisible
    );

    const pendingFocusOffsetRef = useRef<number | null>(null);
    const pendingClickYRef = useRef<number | null>(null);

    const sectionId = reportSection.id;

    const { sectionData, handleRegenerate, handleEdit } = useSectionData(
      organizationId,
      caseId,
      reportId,
      sectionId,
      reportSection.status,
      isExpanded,
      hasBeenVisible
    );

    const { formatSection, isFormatting } = useFormatSection(
      organizationId,
      caseId,
      reportId,
      sectionId
    );

    const editMdast = useMemo(
      () => getEditMdast?.(sectionData) ?? null,
      [getEditMdast, sectionData]
    );
    const isMdastSection = editMdast !== null;

    const handleFormat = async (instructions: string) => {
      const content = sectionData?.content;
      if (!content) return;

      try {
        const formattedContent = await formatSection(content, instructions);
        if (formattedContent) {
          await (isMdastSection && deriveContentMdast
            ? handleEdit(deriveContentMdast(formattedContent))
            : handleEdit(formattedContent));
          toast({
            title: formatMessage({ id: "report.format.success" }),
          });
        }
      } catch {
        toast({
          title: formatMessage({ id: "report.format.error" }),
          variant: "destructive",
        });
      }
    };

    const sources = (sectionData?.sources ?? []) as SourceReference[];

    const sourceMapLinkExtensions = useMemo(
      () => [
        SourceMapLink,
        ...(isMdastSection ? reportMdastEditorExtensions : []),
        ReportReferenceSlashCommand.configure({
          onOpen: setReferenceMenuEditor,
        }),
      ],
      [isMdastSection]
    );

    const isEditContentEmpty = useMemo(() => {
      if (isMdastSection) return !hasTiptapContent(editJsonContent);
      const markdown = htmlToMarkdown(editContent);
      return markdown.trim() === "";
    }, [editContent, editJsonContent, isMdastSection]);

    const isProcessing =
      reportSection.status === "processing" ||
      reportSection.status === "pending";
    const isSectionFailed = reportSection.status === "failed";

    const handleStartEdit = useCallback(() => {
      trackEvent({
        eventName: "app/report/section_edit.button/click",
        properties: {
          sectionId: reportSection.id,
          sectionTitle: reportSection.title,
        },
      });
      if (editMdast) {
        setEditJsonContent(reportMdastToTiptap(editMdast));
      } else {
        setEditContent(sectionData?.content ?? "");
      }
      setIsEditing(true);
    }, [reportSection.id, reportSection.title, sectionData, editMdast]);

    const handleContentDoubleClick = useCallback(
      (e: React.MouseEvent) => {
        if (
          !isActionAvailable ||
          isProcessing ||
          (!sectionData?.content && !editMdast)
        )
          return;
        if ((e.target as HTMLElement).closest("a")) return;

        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const preRange = document.createRange();
          preRange.selectNodeContents(e.currentTarget);
          preRange.setEnd(range.startContainer, range.startOffset);
          pendingFocusOffsetRef.current = preRange.toString().length;
        }

        pendingClickYRef.current = e.clientY;

        trackEvent({
          eventName: "app/report/section_edit.button/click",
          properties: {
            sectionId: reportSection.id,
            sectionTitle: reportSection.title,
          },
        });
        if (editMdast) {
          setEditJsonContent(reportMdastToTiptap(editMdast));
        } else {
          setEditContent(sectionData?.content ?? "");
        }
        setIsEditing(true);
      },
      [
        isActionAvailable,
        isProcessing,
        reportSection.id,
        reportSection.title,
        sectionData,
        editMdast,
      ]
    );

    const handleCancelEdit = useCallback(() => {
      setEditContent("");
      setEditJsonContent(undefined);
      setIsImageUploadPending(false);
      setIsEditing(false);
    }, []);

    const handleSaveEdit = useCallback(async () => {
      setIsSaving(true);
      try {
        const content = isMdastSection
          ? editJsonContent && tiptapToReportMdast(editJsonContent)
          : htmlToMarkdown(editContent);
        if (!content) return;
        trackEvent({
          eventName: "app/report/section_edit_save.button/click",
          properties: {
            sectionId: reportSection.id,
          },
        });
        await handleEdit(content);
        setIsEditing(false);
        setEditContent("");
        setEditJsonContent(undefined);
        setIsImageUploadPending(false);
      } catch (error) {
        logger.error("Failed to save section:", error);
      } finally {
        setIsSaving(false);
      }
    }, [
      editContent,
      editJsonContent,
      isMdastSection,
      handleEdit,
      reportSection.id,
    ]);

    useEffect(() => {
      if (!isExpanded && isEditing) {
        setIsEditing(false);
        setEditContent("");
        setEditJsonContent(undefined);
        setIsImageUploadPending(false);
        setReferenceMenuEditor(null);
      }
    }, [isExpanded, isEditing]);

    const displayTitle = localizeReportSectionTitle(
      reportSection.title,
      templateName,
      outputLanguage
    );

    const renderSectionContent = () => {
      if (isSectionFailed) {
        return (
          <Alert
            variant="default"
            className="border-red-200 bg-red-50 px-3 py-1.5"
          >
            <div className="flex items-center gap-2">
              <CircleAlert className="h-4 w-4 text-red-500 shrink-0" />
              <AlertDescription className="flex-1 text-red-800 text-xs">
                {formatMessage({ id: "chat.report.sectionFailed" })}
              </AlertDescription>
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="shrink-0 text-red-700 hover:bg-red-100 hover:text-red-900 gap-1"
                  onClick={handleRegenerate}
                  disabled={isReportOutdated}
                  title={
                    isReportOutdated
                      ? formatMessage({
                          id: "chat.report.sectionRewriteOutdatedHint",
                        })
                      : undefined
                  }
                >
                  <RefreshCw className="h-3 w-3" />
                  <FormattedMessage
                    id="report.rewrite"
                    defaultMessage="Rewrite"
                  />
                </Button>
              )}
            </div>
          </Alert>
        );
      }

      if (isEditing) {
        return (
          <div className="flex flex-col gap-2">
            <NoteEditor
              content={editContent}
              onChange={setEditContent}
              jsonContent={editJsonContent}
              onJsonChange={isMdastSection ? setEditJsonContent : undefined}
              placeholder={formatMessage({
                id: "report.edit.placeholder",
                defaultMessage: "Edit section content...",
              })}
              className="border border-border rounded-md"
              contentClassName="min-h-0"
              textSize="sm"
              showToolbar={true}
              stickyToolbar={true}
              enableImages={true}
              organizationId={organizationId}
              projectId={caseId}
              imageInputRef={imageInputRef}
              onImageUploadPendingChange={setIsImageUploadPending}
              extraExtensions={sourceMapLinkExtensions}
            >
              {(editor) => {
                if (pendingFocusOffsetRef.current !== null) {
                  const targetOffset = pendingFocusOffsetRef.current;
                  pendingFocusOffsetRef.current = null;
                  let charCount = 0;
                  let targetPos: number | null = null;
                  editor.state.doc.descendants((node, pos) => {
                    if (targetPos !== null) return false;
                    if (node.isText && node.text) {
                      if (charCount + node.text.length >= targetOffset) {
                        targetPos = pos + (targetOffset - charCount);
                        return false;
                      }
                      charCount += node.text.length;
                    }
                    return true;
                  });
                  const finalPos = targetPos;
                  const savedClickY = pendingClickYRef.current;
                  pendingClickYRef.current = null;
                  requestAnimationFrame(() => {
                    if (finalPos !== null) {
                      const maxPos = editor.state.doc.content.size;
                      const clamped = Math.min(Math.max(finalPos, 0), maxPos);
                      editor.chain().focus().setTextSelection(clamped).run();

                      if (savedClickY !== null) {
                        requestAnimationFrame(() => {
                          try {
                            const coords = editor.view.coordsAtPos(clamped);
                            const delta = coords.top - savedClickY;
                            let el: HTMLElement | null =
                              editor.view.dom.parentElement;
                            while (el) {
                              const { overflowY } = getComputedStyle(el);
                              if (
                                overflowY === "auto" ||
                                overflowY === "scroll"
                              ) {
                                el.scrollTop += delta;
                                break;
                              }
                              el = el.parentElement;
                            }
                          } catch {
                            // Scroll adjustment is best-effort
                          }
                        });
                      }
                    } else {
                      editor.commands.focus();
                    }
                  });
                }
                return referenceMenuEditor === editor ? (
                  <ReportReferenceSlashMenu
                    editor={editor}
                    documents={referenceDocuments}
                    onSelectImage={() => imageInputRef.current?.click()}
                    onClose={() => setReferenceMenuEditor(null)}
                  />
                ) : null;
              }}
            </NoteEditor>
            <div className="flex items-center gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancelEdit}
                disabled={isSaving || isImageUploadPending}
              >
                <X className="w-3 h-3 mr-1" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={
                  isSaving || isImageUploadPending || isEditContentEmpty
                }
              >
                {isSaving ? (
                  <FormattedMessage
                    id="common.saving"
                    defaultMessage="Saving..."
                  />
                ) : isImageUploadPending ? (
                  <FormattedMessage
                    id="report.edit.image.uploading"
                    defaultMessage="Uploading Image..."
                  />
                ) : (
                  <FormattedMessage
                    id="report.edit.save"
                    defaultMessage="Save Changes"
                  />
                )}
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div ref={lazyRef}>
          {hasBeenVisible ? (
            <>
              <div
                onDoubleClick={handleContentDoubleClick}
                className={isActionAvailable ? "cursor-text" : undefined}
              >
                {renderDisplayContent ? (
                  renderDisplayContent(sectionData)
                ) : (
                  <ReleaseModeReportContent
                    content={sectionData?.content ?? ""}
                    contentData={sectionData}
                    terms={terms}
                    medicationMatcher={medicationMatcher}
                  />
                )}
              </div>
              {sources.length > 0 && (
                <ReportSectionSources
                  sources={sources}
                  onSourceClick={onSourceClick}
                />
              )}
            </>
          ) : (
            <div className="animate-pulse min-h-[150px]">
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[330px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[300px] mb-2.5" />
              <Skeleton className="h-3 bg-muted rounded-full max-w-[360px] mb-2.5" />
            </div>
          )}
        </div>
      );
    };

    return (
      <AccordionItem
        value={accordionValue}
        className="group border-none relative"
        disabled={isProcessing}
      >
        <div className="sticky top-0 z-10 bg-background pt-1 pb-[2px] -mt-1">
          <AccordionTrigger className="py-0 hover:no-underline" hideChevron>
            <h2 className="text-sm font-semibold flex items-center gap-1.5">
              <span className="wrap-break-word min-w-0">{displayTitle}</span>
              {isProcessing ? (
                <LoadingSpinner className="size-4 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronDownIcon className="size-4 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
              )}
            </h2>
          </AccordionTrigger>
        </div>
        <AccordionContent className={cn("pt-2", isEditing ? "pb-2" : "pb-0")}>
          {renderSectionContent()}
        </AccordionContent>
        {!isProcessing && !isSectionFailed && !isEditing && (
          <div className="h-8 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity duration-200 mt-0">
            <div className="inline-flex items-center gap-2">
              <ContentCopyButton
                content={sectionData?.content ?? ""}
                preCopyMessageId="copy"
                color="muted-foreground"
                eventName="app/report/section_copy.button/click"
              />
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                  onClick={handleStartEdit}
                  disabled={isProcessing}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  <FormattedMessage id="report.edit" defaultMessage="Edit" />
                </Button>
              )}
              {isActionAvailable && (
                <Button
                  variant="ghost"
                  size="mini"
                  className="text-muted-foreground hover:text-muted-foreground inline-flex items-center px-0 text-xs hover:bg-transparent"
                  onClick={() => {
                    trackEvent({
                      eventName: "app/report/section_rewrite.button/click",
                      properties: {
                        sectionId: reportSection.id,
                        sectionTitle: reportSection.title,
                      },
                    });
                    handleRegenerate();
                  }}
                  disabled={isProcessing || isReportOutdated}
                  title={
                    isReportOutdated
                      ? formatMessage({
                          id: "chat.report.sectionRewriteOutdatedHint",
                        })
                      : undefined
                  }
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  <FormattedMessage
                    id="report.rewrite"
                    defaultMessage="Rewrite"
                  />
                </Button>
              )}
              {isActionAvailable && (
                <FormatSectionInput
                  sectionId={reportSection.id}
                  sectionTitle={reportSection.title}
                  isFormatting={isFormatting}
                  disabled={isProcessing}
                  onFormat={handleFormat}
                />
              )}
            </div>
          </div>
        )}
      </AccordionItem>
    );
  }
);
